import { ArrowRight, Clock, ExternalLink, History, StickyNote } from "lucide-react";
import { useState } from "react";
import { OwnerAvatar } from "@/components/owner-avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAllocLog } from "@/hooks/use-alloc-log";
import type { Machine } from "@/lib/types";

function formatDuration(startTimestamp: string, endTimestamp: string | null): string {
  if (!endTimestamp) return "ongoing";
  const milliseconds = new Date(endTimestamp).getTime() - new Date(startTimestamp).getTime();
  if (milliseconds < 0) return "-";
  const totalMinutes = Math.floor(milliseconds / 60000);
  if (totalMinutes < 1) return "<1m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

export function MachineHistoryDialog({
  machine,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: {
  machine: Machine;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChangeProp?.(nextOpen);
  };

  const { data: allocLog, isLoading } = useAllocLog(open ? machine.id : null);

  const sessions: Array<{
    user: string;
    acquiredAt: string;
    releasedAt: string | null;
    duration: string;
    isOngoing: boolean;
    note: string | null;
    holdNote: string | null;
  }> = [];

  if (allocLog) {
    const sorted = [...allocLog].sort(
      (entryA, entryB) =>
        new Date(entryB.timestamp).getTime() - new Date(entryA.timestamp).getTime(),
    );

    // `marked_disposable` is absent on purpose: the mark keeps the lease, so the
    // session is still ongoing.
    const SESSION_END_ACTIONS = new Set([
      "released",
      "auto_released",
      "admin_released",
      "watchdog_released",
      "transferred",
    ]);

    let pendingRelease: string | null = null;
    let pendingHoldNote: string | null = null;
    for (const entry of sorted) {
      if (SESSION_END_ACTIONS.has(entry.action)) {
        pendingRelease = entry.timestamp;
        // The holder's note, snapshotted at release time.
        pendingHoldNote = entry.hold_note ?? null;
      } else if (entry.action === "acquired") {
        const isOngoing = pendingRelease === null;
        sessions.push({
          user: entry.user_name || "Unknown",
          acquiredAt: entry.timestamp,
          releasedAt: pendingRelease,
          duration: formatDuration(
            entry.timestamp,
            isOngoing ? new Date().toISOString() : pendingRelease,
          ),
          isOngoing,
          note: entry.note ?? null,
          // Active session has no release row yet — show the live machine note.
          holdNote: isOngoing ? (machine.note ?? null) : pendingHoldNote,
        });
        pendingRelease = null;
        pendingHoldNote = null;
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <Tooltip>
          <TooltipTrigger className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground">
            <SheetTrigger className="inline-flex h-full w-full items-center justify-center">
              <History className="h-3.5 w-3.5" />
            </SheetTrigger>
          </TooltipTrigger>
          <TooltipContent>Allocation history</TooltipContent>
        </Tooltip>
      )}
      <SheetContent className="w-[440px] sm:w-[520px]">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-base font-semibold">{machine.name}</SheetTitle>
          <p className="text-sm text-muted-foreground">Allocation History</p>
        </SheetHeader>

        <div className="space-y-1">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <Clock className="h-8 w-8 opacity-30" />
              <p className="text-sm">No allocation history yet.</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.acquiredAt}
                className="flex items-center gap-4 rounded-lg border px-4 py-3"
              >
                <div className="shrink-0">
                  <OwnerAvatar
                    name={session.user}
                    isService={session.note?.includes("CI cleanup")}
                    size="lg"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{session.user}</span>
                    {session.isOngoing && (
                      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                        active
                      </span>
                    )}
                    {session.note?.startsWith("http") && (
                      <a
                        href={session.note}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-3 w-3" />
                        CI run
                      </a>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                    <span className="font-mono">{formatTimestamp(session.acquiredAt)}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span className="font-mono">
                      {session.releasedAt ? formatTimestamp(session.releasedAt) : "now"}
                    </span>
                  </div>
                  {session.holdNote && (
                    <div className="mt-1 flex items-start gap-1.5 text-[12px] text-foreground/70">
                      <StickyNote className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 break-words whitespace-pre-wrap">
                        {session.holdNote}
                      </span>
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <span className="font-mono text-sm font-medium">{session.duration}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
